// Barrelcast — usePushSubscription
// Registers the browser for Web Push and saves the subscription to Supabase

import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const VAPID_PUBLIC_KEY = import.meta.env.VITE_VAPID_PUBLIC_KEY ?? "BNoA3v-dxJ7NVtu-CzV2g89g6AIvhKl_Wd8JJKv4Ha6JtyqC9fZqjH8c9D1_YcRtzIHTDFuPU4Rd919fuqurW3w";

function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function usePushSubscription() {
  const [subscribing, setSubscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const subscribe = async (userId: string): Promise<boolean> => {
    setSubscribing(true);
    setError(null);

    try {
      // Check browser support
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        throw new Error("Push notifications aren't supported in this browser");
      }

      // Request permission
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        throw new Error("Notification permission denied");
      }

      // Get service worker registration
      const registration = await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });

      // Save to Supabase
      const { error: dbError } = await supabase
        .from("profiles")
        .update({ push_subscription: subscription.toJSON() } as any)
        .eq("id", userId);

      if (dbError) throw dbError;

      return true;
    } catch (err: any) {
      setError(err.message || "Failed to enable notifications");
      return false;
    } finally {
      setSubscribing(false);
    }
  };

  const unsubscribe = async (userId: string): Promise<boolean> => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();

      await supabase
        .from("profiles")
        .update({ push_subscription: null, alert_enabled: false } as any)
        .eq("id", userId);

      return true;
    } catch (err: any) {
      setError(err.message);
      return false;
    }
  };

  return { subscribe, unsubscribe, subscribing, error };
}
